import { useState, useEffect, useCallback } from 'react';
import isEqual from 'lodash.isequal';
import {
  Popup,
  formatBalance,
  InputText,
  ButtonPrimary,
  ButtonPseudo,
  ButtonSecondary,
  ButtonSize,
  IconSettings,
  SelectItemProps,
  IconSelected,
  IconBlock,
  IconEye,
  IconEyeOff,
  SidePanel,
  SidePanelType,
  Checkbox,
  IconDownload,
  ToggleInput,
  ResponseError,
  PermissionEnum,
  ChangeLogs,
  PermissionDenied,
  DateInput,
  formatDate,
  IconLoader,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import Card from '../../partial/card/card';
import { CustomerStatus } from '../../../hoc/customers/types';
import {
  clearSettingsPanel,
  clearClosingBalances,
} from '../../../hoc/individual/store/slices/accounts/accounts';
import AccountBalance from '../../partial/balance/balance';
import {
  useUpdateCustomersOverdraftStatusMutation,
  useLazyGetCustomersAccountsChangeLogsQuery,
} from '../../../hoc/customers/store/services/customers';
import { useLazyGetIndividualAccountClosingBalancesQuery } from '../../../hoc/individual/store/services/individual';
import { AccountItemTypes } from '../types';

import cn from './accounts-content.module.css';

const AccountItem = ({
  account,
  balance,
  onBlockTopup,
  onBlockWithdraw,
  blockTopupInProgress,
  blockWithdrawInProgress,
  onClose,
  closeInProgress,
  setUserAccount,
  setTopUpPanelIsOpen,
  closeIsSuccess,
  blockDebitIsSuccess,
  blockCreditIsSuccess,
  closeOpenError,
  blockUblockTopupError,
  blockUblockWithdrawalsError,
  setShowCurrencies,
  setSelectedAccountCurrenciesId,
  setStatementPanelVisible,
  setSelectedAccount,
  setCurrenciesList,
  unblockDebitIsSuccess,
  unblockCreditIsSuccess,
  openIsSuccess,
  onUpdateVisibility,
  updateVisibilityInProgress,
  overdraftEnabled,
  accountsBalances,
  setBalances,
  defaultBalances,
  setDefaultBalances,
  onUpdateOverdraftBalanceValue,
  updateOverdraftBalanceIsLoading,
  customerId,
  status: organizationStatus,
  closedBalances,
}: AccountItemTypes): React.JSX.Element => {
  const [blockWithdrawPopupVisible, setBlockWithdrawPopupVisible] =
    useState(false);
  const [blockTopupPopupVisible, setBlockTopupPopupVisible] = useState(false);
  const [closePopupVisible, setClosePopupVisible] = useState(false);
  const [creditBlock, setCreditBlock] = useState(false);
  const [debitBlock, setDebitBlock] = useState(false);
  const [closed, setClosed] = useState(false);
  const [comment, setComment] = useState('');
  const [settingsPanelVisible, setSettingsPanelVisible] = useState(false);
  const [visibleToClient, setVisibleToClient] = useState(false);
  const [selectedTab, setSelectedTab] = useState('settings');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountOverdraftEnabled, setOverdraftEnabled] = useState(false);
  const [closingBalancesAt, setClosingBalancesAt] = useState('');

  const [updateOverdraftStatus, { isLoading: overdraftUpdateInProgress }] =
    useUpdateCustomersOverdraftStatusMutation();
  const [
    getIndividualCustomerClosingBalances,
    { isLoading: balancesIsLoading, isFetching: balancesIsFetching },
  ] = useLazyGetIndividualAccountClosingBalancesQuery();

  const getIndividualCustomerClosingBalancesAction = async ({
    accountId,
    date,
  }: {
    accountId: string;
    date?: string;
  }) => {
    const body: {
      id: string;
      closingBalancesAt?: string;
    } = {
      id: accountId,
    };

    if (date) {
      body.closingBalancesAt = date;
    }

    await getIndividualCustomerClosingBalances(
      `?accountId=${body.id}&date=${body.closingBalancesAt}`,
    );
  };

  const onUpdateOverdraftStatus = async ({
    enabled,
    accountId,
  }: {
    enabled: boolean;
    accountId: string;
  }) => {
    setOverdraftEnabled(enabled);

    const response = (await updateOverdraftStatus({
      id: customerId as string,
      accountId,
      overdraftEnabled: enabled,
    })) as { error?: ResponseError };

    if (response.error) {
      setOverdraftEnabled(!enabled);
    }
  };

  const dispatch = useAppDispatch();

  const [getAccountsLogs, { isLoading, isFetching }] =
    useLazyGetCustomersAccountsChangeLogsQuery();

  const {
    customersDictionaries: {
      currencies,
      baseCurrency,
      currenciesList,
      activePermissions,
    },
    individualCustomers: {
      info: {
        accounts: { accountsLogs, accountsLogsPagination },
      },
    },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);
  const { ffSettings } = useAppSelector((state) => state.root);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getAccountsLogs(
        `?take=20&entityType=ACCOUNT&entityId=${selectedAccountId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [selectedAccountId],
  );

  useEffect(() => {
    if (blockCreditIsSuccess || unblockCreditIsSuccess) {
      setBlockTopupPopupVisible(false);
    }
  }, [blockCreditIsSuccess, unblockCreditIsSuccess, selectedAccountId]);

  useEffect(() => {
    if (blockDebitIsSuccess || unblockDebitIsSuccess) {
      setBlockWithdrawPopupVisible(false);
    }
  }, [blockDebitIsSuccess, unblockDebitIsSuccess, selectedAccountId]);

  useEffect(() => {
    if (closeIsSuccess || openIsSuccess) {
      setClosePopupVisible(false);
      setComment('');
    }
  }, [closeIsSuccess, openIsSuccess]);

  useEffect(() => {
    if (account && account.overdraftEnabled && overdraftEnabled) {
      setOverdraftEnabled(true);
    }
  }, [account, settingsPanelVisible, overdraftEnabled]);

  useEffect(() => {
    if (
      selectedTab === 'logs' &&
      !accountsLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      settingsPanelVisible
    ) {
      getChangeLogsAction();
    }
  }, [selectedTab, settingsPanelVisible]);

  let status;

  if (account) {
    if (account.closed) {
      status = 'Account closed';
    } else if (!account.closed && account?.debitBlock && account.creditBlock) {
      status = 'Account blocked';
    } else if (!account.closed && !account?.debitBlock && account.creditBlock) {
      status = 'Blocked for top-up';
    } else if (!account.closed && account?.debitBlock && !account.creditBlock) {
      status = 'Withdrawal blocked';
    } else if (
      !account.closed &&
      !account?.debitBlock &&
      !account.creditBlock
    ) {
      if (account?.active) {
        status = 'Active';
      } else {
        status = 'Inactive';
      }
    }
  }

  useEffect(() => {
    if (balance && settingsPanelVisible && !accountsBalances) {
      setBalances(balance);

      setDefaultBalances(balance);
    }
  }, [settingsPanelVisible, balance, accountsBalances]);

  useEffect(() => {
    if (account) {
      setDebitBlock(account.debitBlock);
      setCreditBlock(account.creditBlock);
      setClosed(account.closed);
      setVisibleToClient(account.visibleToClient);
    }
  }, [account]);

  const baseCurrencyBalance = balance?.find(
    (item) => item.currencyId === baseCurrency?.id,
  );

  const balanceWithCurrencyName = balance?.map((item) => ({
    ...item,
    currencyName:
      currencies?.find((currency) => item.currencyId === currency.value)
        ?.suffix ||
      item?.currency?.name ||
      '',
  }));

  return (
    <Card className={cn.accountCard}>
      <div className={cn.accountItemTop}>
        <div className={cn.accountItemTitleWrapper}>
          <div className={cn.accountItemTitle}>
            {account?.fullNumber}
            <div className={cn.accountCreatedDate}>
              {`created on ${new Date(
                account?.createdAt as string,
              ).toLocaleDateString()}`}
            </div>
          </div>
          <div className={cn.accountTotal} style={{ display: 'none' }}>
            {`${formatBalance(
              account?.totalBalanceBC || 0,
              currenciesList?.find((item) => item.id === baseCurrency?.id),
            )} ${baseCurrency?.name}`}
            <span className={cn.accountTotalHint}>bc</span>
          </div>
        </div>
        <div>
          <div className={cn.accountStatus}>
            {overdraftEnabled &&
              account?.overdraftEnabled &&
              overdraftEnabled && (
                <div className={cn.overdraftStatus}>Overdraft</div>
              )}
            <div
              className={`${cn.accountStatusValue} ${
                !account?.active ||
                account.debitBlock ||
                account.creditBlock ||
                account.closed
                  ? cn.accountStatusInactive
                  : ''
              }`}
            >
              {status === 'Active' ? (
                <div className={cn.iconWrapper}>
                  <IconSelected className={cn.statusIcon} />
                </div>
              ) : (
                <div className={cn.iconWrapper}>
                  <IconBlock className={cn.statusIcon} />
                </div>
              )}
              <span>{status}</span>
            </div>
            <div
              className={`${cn.accountStatusValue} ${
                !account?.visibleToClient ? cn.accountStatusInactive : ''
              } ${cn.accountVisibility}`}
            >
              {account?.visibleToClient ? (
                <div className={cn.iconWrapper}>
                  <IconEye width={16} height={16} className={cn.statusIcon} />
                </div>
              ) : (
                <div className={cn.iconWrapper}>
                  <IconEyeOff
                    width={16}
                    height={16}
                    className={cn.statusIcon}
                  />
                </div>
              )}
              <span>
                {account?.visibleToClient ? 'Visible' : 'Not Visible'}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className={cn.accountCurrenciesWrapper}>
        {baseCurrencyBalance && (
          <div className={cn.accountCurrency}>
            <div className={cn.accountCurrencyName}>
              {`${
                currencies?.find(
                  (currency) =>
                    baseCurrencyBalance.currencyId === currency.value,
                )?.suffix
              }:`}
            </div>
            {formatBalance(
              baseCurrencyBalance.value,
              currenciesList?.find(
                (item) => item.id === baseCurrencyBalance.currencyId,
              ),
            )}
            {baseCurrencyBalance?.overdraftEnabled &&
              overdraftEnabled &&
              accountOverdraftEnabled && (
                <div className={cn.overdraftValue}>
                  {formatBalance(
                    baseCurrencyBalance.overdraftValue as number,
                    currenciesList?.find(
                      (item) => item.id === baseCurrencyBalance.currencyId,
                    ),
                  )}
                </div>
              )}
          </div>
        )}
        {balanceWithCurrencyName
          ?.filter(
            (item) => item.currencyId !== baseCurrencyBalance?.currencyId,
          )
          .sort((a, b) => a?.currencyName?.localeCompare(b?.currencyName))
          .map((item) => (
            <div className={cn.accountCurrency} key={item.id}>
              <div className={cn.accountCurrencyName}>
                {`${item.currencyName}:`}
              </div>
              {formatBalance(
                item.value,
                currenciesList?.find(
                  (currency) => currency.id === item.currencyId,
                ),
              )}
              {item?.overdraftEnabled &&
                overdraftEnabled &&
                accountOverdraftEnabled && (
                  <div className={cn.overdraftValue}>
                    {formatBalance(
                      item.overdraftValue as number,
                      currenciesList?.find(
                        (currency) => currency.id === item.currencyId,
                      ),
                    )}
                  </div>
                )}
            </div>
          ))}
      </div>
      <div className={cn.accountItemBottom}>
        <ButtonPseudo
          text="Available currencies"
          size={ButtonSize.SMALL}
          className={`${cn.currenciesButton} button-white`}
          icon={
            <IconSettings width={16} height={16} className={cn.settingsIcon} />
          }
          onClick={() => {
            setShowCurrencies(true);
            setSelectedAccountCurrenciesId(account?.id as string);
          }}
        />
        <div className={cn.accountItemControls}>
          <ButtonPseudo
            text="Top up options"
            size={ButtonSize.SMALL}
            className={`${cn.topUpButton} ${cn.currenciesButton} button-white`}
            icon={
              <IconSettings
                width={16}
                height={16}
                className={cn.settingsIcon}
              />
            }
            onClick={() => {
              setUserAccount(account?.id as string);
              setTopUpPanelIsOpen(true);
            }}
          />
          <ButtonPseudo
            text="Statement"
            size={ButtonSize.SMALL}
            className={`${cn.withMargin} ${cn.downloadStatementButton} ${cn.currenciesButton} button-white`}
            icon={<IconDownload />}
            onClick={() => {
              setStatementPanelVisible(true);
              setSelectedAccount(account?.id as string);

              setCurrenciesList(
                balanceWithCurrencyName?.map((item) => ({
                  name: item.currencyName,
                  value: item.currencyId,
                })) as SelectItemProps[],
              );
            }}
          />
          <ButtonSecondary
            text="Settings"
            size={ButtonSize.SMALL}
            className={`${cn.currenciesButton} button-white`}
            onClick={() => {
              setSettingsPanelVisible(true);
              setSelectedAccountId(account?.id as string);
            }}
          />
        </div>
      </div>
      <SidePanel
        isOpen={settingsPanelVisible}
        title="Account settings"
        type={SidePanelType.Accounts}
        onClose={() => {
          setSettingsPanelVisible(false);
          setSelectedAccountId('');
          setSelectedTab('settings');
          setOverdraftEnabled(false);
          setBalances(undefined);
          setDefaultBalances(undefined);
          setClosingBalancesAt('');

          dispatch(clearSettingsPanel());
        }}
        sidepanelExpandedKey="individual-account-settings"
        footer={
          <>
            {organizationStatus !== CustomerStatus.CLOSED && (
              <ButtonPrimary
                text={
                  updateVisibilityInProgress || updateOverdraftBalanceIsLoading
                    ? 'Loading...'
                    : 'Save'
                }
                onClick={() => {
                  if (account?.visibleToClient !== visibleToClient) {
                    onUpdateVisibility({
                      id: account?.id as string,
                      visibleToClientValue: visibleToClient,
                    });
                  }

                  if (!isEqual(defaultBalances, accountsBalances)) {
                    onUpdateOverdraftBalanceValue(account?.id as string);
                  }
                }}
                disabled={
                  isEqual(defaultBalances, accountsBalances) &&
                  account?.visibleToClient === visibleToClient
                }
                bold
              />
            )}

            <ButtonSecondary
              text="Cancel"
              onClick={() => {
                setSettingsPanelVisible(false);
                setSelectedAccountId('');
                setSelectedTab('settings');
                setOverdraftEnabled(false);
                setBalances(undefined);
                setDefaultBalances(undefined);
                setClosingBalancesAt('');

                dispatch(clearSettingsPanel());
              }}
              bold
            />
          </>
        }
        closeOnClickOutside
      >
        <div className={cn.accountSettingsContent}>
          <div className={cn.accountTabs}>
            <button
              type="button"
              className={`${cn.accountTab} ${
                selectedTab === 'settings' ? cn.active : ''
              }`}
              onClick={() => setSelectedTab('settings')}
            >
              Settings
            </button>
            <button
              type="button"
              className={`${cn.accountTab} ${
                selectedTab === 'logs' ? cn.active : ''
              }`}
              onClick={() => setSelectedTab('logs')}
            >
              Logs
            </button>
          </div>
          {selectedTab === 'settings' && (
            <>
              <div className={cn.accountSettingsTitle}>Account blocking</div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="checkbox"
                  name={`creditBlock-${account?.id}`}
                  checked={creditBlock}
                  onChange={(e) => {
                    setCreditBlock(e.target.checked);

                    setBlockTopupPopupVisible(true);
                  }}
                  title="Block top-up"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="checkbox"
                  name={`debitBlock-${account?.id}`}
                  checked={debitBlock}
                  onChange={(e) => {
                    setDebitBlock(e.target.checked);

                    setBlockWithdrawPopupVisible(true);
                  }}
                  title="Block withdraw"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>
              <div className={cn.accountSettingsTitle}>Account visibility</div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="radio"
                  name={`visibleToClient-${account?.id}`}
                  checked={visibleToClient}
                  onChange={() => setVisibleToClient(true)}
                  title="Visible to the client"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>
              <div className={cn.blockAccountWrapper}>
                <Checkbox
                  type="radio"
                  name={`notVisibleToClient-${account?.id}`}
                  checked={!visibleToClient}
                  onChange={() => setVisibleToClient(false)}
                  title="Not visible to the client"
                  disabled={organizationStatus === CustomerStatus.CLOSED}
                />
              </div>

              <div className={cn.balances}>
                <div className={cn.closingBalanceWrapper}>
                  <DateInput
                    placeholder="Select balance date"
                    value={closingBalancesAt}
                    onChange={(e) => {
                      setClosingBalancesAt(new Date(e).toISOString());

                      getIndividualCustomerClosingBalancesAction({
                        accountId: account?.id as string,
                        date: new Date(e).toISOString(),
                      });
                    }}
                    onReset={() => {
                      setClosingBalancesAt('');

                      dispatch(clearClosingBalances());
                    }}
                    withoutCustomSelector
                  />
                </div>

                <div
                  className={`${cn.balancesTop} ${
                    closedBalances ? cn.flexStart : ''
                  }`}
                >
                  <div className={cn.balancesTitle}>Balance</div>
                  {closedBalances && (
                    <div className={cn.balancesTitle}>{`Balance on ${formatDate(
                      closingBalancesAt,
                    )}`}</div>
                  )}
                  {overdraftEnabled && (
                    <div className={cn.overdraftToggleWrapper}>
                      <ToggleInput
                        name={`accountOverdraftEnabled-${account?.id}`}
                        checked={accountOverdraftEnabled}
                        title="Overdraft"
                        onChange={(e) =>
                          onUpdateOverdraftStatus({
                            enabled: e.target.checked,
                            accountId: account?.id as string,
                          })
                        }
                        disabled={
                          overdraftUpdateInProgress ||
                          organizationStatus === CustomerStatus.CLOSED
                        }
                      />
                    </div>
                  )}
                </div>
                <div className={cn.balancesWrapper}>
                  {(balancesIsLoading || balancesIsFetching) && (
                    <div className={cn.closingBalancesLoader}>
                      <IconLoader />
                    </div>
                  )}
                  {baseCurrencyBalance && (
                    <AccountBalance
                      balance={{
                        ...baseCurrencyBalance,
                        currencyName: currencies?.find(
                          (currency) =>
                            currency.value === baseCurrencyBalance.currencyId,
                        )?.suffix,
                      }}
                      currenciesList={currenciesList}
                      overdraftEnabled={
                        overdraftEnabled && accountOverdraftEnabled
                      }
                      balances={accountsBalances}
                      setBalances={setBalances}
                      updateOverdraftBalanceIsLoading={
                        updateOverdraftBalanceIsLoading
                      }
                      defaultBalance={defaultBalances?.find(
                        (balanceValue) =>
                          balanceValue.id === baseCurrencyBalance.id,
                      )}
                      closedBalance={
                        closedBalances
                          ?.find(
                            (item) =>
                              item.currencyId ===
                              baseCurrencyBalance.currencyId,
                          )
                          ?.value?.toString()
                          ? formatBalance(
                              closedBalances?.find(
                                (item) =>
                                  item.currencyId ===
                                  baseCurrencyBalance.currencyId,
                              )?.value as number,
                              currenciesList?.find(
                                (item) =>
                                  item.id === baseCurrencyBalance.currencyId,
                              ),
                            )
                          : undefined
                      }
                    />
                  )}
                  {balanceWithCurrencyName
                    ?.filter(
                      (item) =>
                        item.currencyId !== baseCurrencyBalance?.currencyId,
                    )
                    .sort((a, b) =>
                      a?.currencyName?.localeCompare(b?.currencyName),
                    )
                    .map((item) => (
                      <AccountBalance
                        key={item.id}
                        balance={item}
                        currenciesList={currenciesList}
                        overdraftEnabled={
                          overdraftEnabled && accountOverdraftEnabled
                        }
                        balances={accountsBalances}
                        setBalances={setBalances}
                        updateOverdraftBalanceIsLoading={
                          updateOverdraftBalanceIsLoading
                        }
                        defaultBalance={defaultBalances?.find(
                          (balanceValue) => balanceValue.id === item.id,
                        )}
                        closedBalance={
                          closedBalances
                            ?.find(
                              (balanceValue) =>
                                balanceValue.currencyId === item.currencyId,
                            )
                            ?.value?.toString()
                            ? formatBalance(
                                closedBalances?.find(
                                  (balanceValue) =>
                                    balanceValue.currencyId === item.currencyId,
                                )?.value as number,
                                currenciesList?.find(
                                  (currency) => currency.id === item.currencyId,
                                ),
                              )
                            : undefined
                        }
                      />
                    ))}
                </div>
              </div>
              {organizationStatus !== CustomerStatus.CLOSED &&
                (!closed ||
                  (closed && !ffSettings?.ffAccountReopenDisabled)) && (
                  <div className={cn.closeAccountWrapper}>
                    <button
                      type="button"
                      className={`${cn.closeAccountButton} ${
                        closed ? cn.openAccountButton : ''
                      }`}
                      onClick={() => setClosePopupVisible(true)}
                    >
                      {`${closed ? 'Open' : 'Close'} account`}
                    </button>
                  </div>
                )}
            </>
          )}
          {selectedTab === 'logs' && (
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="ACCOUNT"
                  entityId={selectedAccountId}
                  changeLogsIsLoading={isLoading}
                  changeLogs={accountsLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={accountsLogsPagination}
                  changeLogsIsFetching={isFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
        </div>
      </SidePanel>
      <Popup
        title={`${!debitBlock ? 'Unblock' : 'Block'} all withdrawals?`}
        onCancelClick={() => setBlockWithdrawPopupVisible(false)}
        onOkClick={() =>
          onBlockWithdraw({
            id: account?.id as string,
            debitBlock: !account?.debitBlock,
          })
        }
        cancelButtonText="Cancel"
        okButtonText={
          blockWithdrawInProgress
            ? 'Loading...'
            : `Yes, ${!debitBlock ? 'Unblock' : 'Block'}`
        }
        isVisible={blockWithdrawPopupVisible}
        parent={document.getElementById('account-popup') as HTMLElement}
      >
        <div>
          {blockUblockWithdrawalsError && (
            <div className={`${cn.accountError} ${cn.blockError}`}>
              {blockUblockWithdrawalsError}
            </div>
          )}
        </div>
      </Popup>
      <Popup
        title={`${
          !creditBlock ? 'Unblock' : 'Block'
        } the top-up of this account`}
        onCancelClick={() => setBlockTopupPopupVisible(false)}
        onOkClick={() =>
          onBlockTopup({
            id: account?.id as string,
            creditBlock: !account?.creditBlock,
          })
        }
        cancelButtonText="Cancel"
        okButtonText={
          blockTopupInProgress
            ? 'Loading...'
            : `Yes, ${!creditBlock ? 'Unblock' : 'Block'}`
        }
        isVisible={blockTopupPopupVisible}
        parent={document.getElementById('account-popup') as HTMLElement}
      >
        <div>
          {blockUblockTopupError && (
            <div className={`${cn.accountError} ${cn.blockError}`}>
              {blockUblockTopupError}
            </div>
          )}
        </div>
      </Popup>
      <Popup
        title={`${closed ? 'Open' : 'Close'}  this account`}
        onCancelClick={() => {
          setClosePopupVisible(false);
          setComment('');
        }}
        isVisible={closePopupVisible}
        parent={document.getElementById('account-popup') as HTMLElement}
      >
        <>
          <div className={cn.commentWrapper}>
            <InputText
              name="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter comment"
            />
          </div>
          {closeOpenError && (
            <div className={cn.accountError}>{closeOpenError}</div>
          )}
          <div className={cn.commentControls}>
            <ButtonPseudo
              text="Cancel"
              onClick={() => {
                setClosePopupVisible(false);
                setComment('');
              }}
              bold
            />
            <ButtonPrimary
              text={
                closeInProgress
                  ? 'Loading...'
                  : `Yes, ${account?.closed ? 'open' : 'close'}`
              }
              onClick={() =>
                onClose({
                  id: account?.id as string,
                  comment,
                  closed: !account?.closed,
                })
              }
              disabled={closeInProgress || !comment}
              bold
            />
          </div>
        </>
      </Popup>
      <div id="account-popup" />
    </Card>
  );
};

export default AccountItem;
